import React, { useState } from "react";
import { Facebook, Twitter, Linkedin, Instagram, MessageCircle, Check, Share2 } from "lucide-react";

/**
 * Social share buttons: Facebook, Twitter/X, WhatsApp, LinkedIn, Instagram.
 *
 * Facebook / Twitter / WhatsApp / LinkedIn: open the platform's share intent URL
 * in a popup window with the article title + URL.
 *
 * Instagram has no web share intent, so we copy the URL to the clipboard and
 * show a 2-second "copied" confirmation so the user can paste it into their
 * story / DM / bio manually.
 */
export default function ShareButtons({ title = "", url = "", className = "" }) {
  const [copiedIG, setCopiedIG] = useState(false);
  const safeTitle = encodeURIComponent(title);
  const safeUrl = encodeURIComponent(url);

  const intents = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${safeUrl}&quote=${safeTitle}`,
    twitter: `https://twitter.com/intent/tweet?text=${safeTitle}&url=${safeUrl}`,
    whatsapp: `https://api.whatsapp.com/send?text=${safeTitle}%20${safeUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${safeUrl}`,
  };

  const open = (href) => {
    window.open(href, "_blank", "noopener,noreferrer,width=640,height=560");
  };

  const shareInstagram = async () => {
    try {
      await navigator.clipboard.writeText(`${title} — ${url}`);
      setCopiedIG(true);
      setTimeout(() => setCopiedIG(false), 2000);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = `${title} — ${url}`;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(ta);
      setCopiedIG(true);
      setTimeout(() => setCopiedIG(false), 2000);
    }
  };

  const btn = "w-9 h-9 rounded-full border border-slate-800 text-neutral-400 hover:text-sky-300 hover:border-sky-400/60 flex items-center justify-center transition group";

  return (
    <div
      data-testid="share-buttons"
      className={`flex items-center gap-2 ${className}`}
    >
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mr-1 flex items-center gap-1.5">
        <Share2 className="w-3 h-3" /> Share
      </span>
      <button
        data-testid="share-facebook"
        aria-label="Share on Facebook"
        onClick={() => open(intents.facebook)}
        className={btn}
        type="button"
      >
        <Facebook className="w-4 h-4" />
      </button>
      <button
        data-testid="share-twitter"
        aria-label="Share on X (Twitter)"
        onClick={() => open(intents.twitter)}
        className={btn}
        type="button"
      >
        <Twitter className="w-4 h-4" />
      </button>
      <button
        data-testid="share-whatsapp"
        aria-label="Share on WhatsApp"
        onClick={() => open(intents.whatsapp)}
        className={btn}
        type="button"
      >
        <MessageCircle className="w-4 h-4" />
      </button>
      <button
        data-testid="share-linkedin"
        aria-label="Share on LinkedIn"
        onClick={() => open(intents.linkedin)}
        className={btn}
        type="button"
      >
        <Linkedin className="w-4 h-4" />
      </button>
      <button
        data-testid="share-instagram"
        aria-label={copiedIG ? "Link copied — paste into Instagram" : "Copy link for Instagram"}
        title={copiedIG ? "Copied! Paste in Instagram story/DM/bio" : "Copy link for Instagram"}
        onClick={shareInstagram}
        className={btn}
        type="button"
      >
        {copiedIG ? <Check className="w-4 h-4 text-emerald-400" /> : <Instagram className="w-4 h-4" />}
      </button>
      {copiedIG && (
        <span
          data-testid="share-instagram-copied"
          className="font-mono text-[10px] uppercase tracking-widest text-emerald-400 ml-1"
        >
          Copied · paste in Instagram
        </span>
      )}
    </div>
  );
}
